import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { throttle } from "lodash";
import { getAllTextMatches, highlightMatch as highlightMatchHelper } from "./editorPopup/findInDocument.helper";

const DEFAULT_SEARCH_OPTIONS = { caseSensitive: false, wholeWord: false };

/**
 *
 *Taken refrence from @findandreplace component
 * @param {Object} options
 * @param {() => import("@progress/kendo-react-editor").ProseMirror.EditorView | null} options.resolveView
 * @param {{ caseSensitive?: boolean, wholeWord?: boolean }} [options.initialSearchOptions]
 * @param {boolean} [options.enabled=true] When false, navigation/search updates are skipped.
 * @param {unknown} [options.revalidateWhen] When this reference changes (e.g. editor `desc`), re-run search if there is a term.
 * @param {number} [options.throttleMs=300]
 * 
 */
export function useFindInDocument({
  resolveView,
  initialSearchOptions = DEFAULT_SEARCH_OPTIONS,
  enabled = true,
  revalidateWhen,
  throttleMs = 300,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState(initialSearchOptions);
  const [matches, setMatches] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const matchesRef = useRef(matches);
  matchesRef.current = matches;

  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const searchOptionsRef = useRef(searchOptions);
  searchOptionsRef.current = searchOptions;

  // run the search
  const runSearch = useCallback(
    (term, options) => {
      const view = resolveView();
      if (!term || !view) {
        setMatches([]);
        setCurrentIndex(-1);
        return;
      }
      const results = getAllTextMatches(term, view.state.doc, options);
      setMatches(results);
      setCurrentIndex(-1);
    },
    [resolveView]
  );

  // throttled search to avoid multiple searches
  const throttledSearch = useMemo(
    () =>
      throttle((term, options) => {
        runSearch(term, options);
      }, throttleMs),
    [runSearch, throttleMs]
  );

  // cancel the search when the component unmounts
  useEffect(() => {
    return () => throttledSearch.cancel();
  }, [throttledSearch]);

  /** Re-run when editor body / mode changes (caller passes `revalidateWhen`) */
  useEffect(() => {
    if (!enabled) return;
    const term = searchTermRef.current;
    if (!term) return;
    throttledSearch(term, searchOptionsRef.current);
  }, [revalidateWhen, enabled, throttledSearch]);

  // go to the next match — re-scan doc so positions match live state (Yjs/first paint can change doc after search).
  const goToNext = useCallback(() => {
    if (!enabled) return;
    const view = resolveView();
    if (!view) return;
    const term = searchTermRef.current;
    if (!term) return;
    const fresh = getAllTextMatches(term, view.state.doc, searchOptionsRef.current);
    setMatches(fresh);
    matchesRef.current = fresh;

    setCurrentIndex((prev) => {
      if (!fresh.length) return -1;
      const next = prev < 0 ? 0 : (prev + 1) % fresh.length;
      highlightMatchHelper(view, fresh[next]);
      return next;
    });
  }, [enabled, resolveView]);

  const goToPrevious = useCallback(() => {
    if (!enabled) return;
    const view = resolveView();
    if (!view) return;
    const term = searchTermRef.current;
    if (!term) return;
    const fresh = getAllTextMatches(term, view.state.doc, searchOptionsRef.current);
    setMatches(fresh);
    matchesRef.current = fresh;

    setCurrentIndex((prev) => {
      if (!fresh.length) return -1;
      const next = prev < 0 ? fresh.length - 1 : (prev - 1 + fresh.length) % fresh.length;
      highlightMatchHelper(view, fresh[next]);
      return next;
    });
  }, [enabled, resolveView]);

  // navigate to the next or previous match
  const navigateMatch = useCallback(
    (direction) => {
      if (direction === "next") goToNext();
      else goToPrevious();
    },
    [goToNext, goToPrevious]
  );

  // clear the search
  const clearFind = useCallback(() => {
    setSearchTerm("");
    setMatches([]);
    setCurrentIndex(-1);
    throttledSearch.cancel();
  }, [throttledSearch]);

  return {
    searchTerm,
    setSearchTerm,
    searchOptions,
    setSearchOptions,
    matches,
    setMatches,
    currentIndex,
    setCurrentIndex,
    throttledSearch,
    goToNext,
    goToPrevious,
    navigateMatch,
    clearFind,
    resolveView,
  };
}